// @flow

import * as React from "react";
import axios from "axios";
import {toast} from "react-toastify";
import PlanCard from "./PlanCard.react";
import ScaleToFit from "./ScaleToFit.react";
import Confetti from "./Confetti.react";
import {showGenericErrorToast, isPaymentDeclined, openPaymentMethodUpdate} from "../Utils";

const UPGRADE_STATUS_TIMEOUT_MS = 45000;
const XHR_HEADERS = {headers: {"X-Requested-With": "XMLHttpRequest"}};

type Props = {
  +userSettings: Object,
  // The modal covers the top bar and can't be closed, so it offers a way to reach support (opens the Crisp chat) and
  // to sign out. Neither gets around choosing a plan.
  +onSignOut: () => void,
};

type State = {
  billingType: number,
  plans: {[number]: Array<Object>},
  switchingPlan: ?Object,
  switchedPlan: ?Object,
  paymentDeclined: boolean,
};

// "QRify is now Scan2Pay" + "your plan is deprecated, choose a new one", shown to the admin of a company still paying
// for a legacy (no longer offered) plan, on every page load. It can't be dismissed (no close button, no Escape, no
// "remember closed"): since 2026-10-12 the only way past it is choosing one of the paid plans (the owner's decision - no
// Free option in it). SiteWrapper skips it for a frozen company, which has to fix its payment method first.
// Choosing a plan switches it right here, with the same server call as the Usage & Plan page
// (/subscriptions/change_plan: the EXISTING Paddle subscription moves to the new plan, prorated and billed now; never a
// second checkout). There's no separate confirm step, so the modal says the switch is immediate and that upgrades are
// billed right away (a customer upgraded and cancelled the same day, surprised by the charge). It says nothing about
// downgrade credits or refunds: Paddle handles the proration. The company's plan
// flips when Paddle's subscription_updated webhook lands, so it polls /check_upgrade_status before showing success.
class LegacyPlanModal extends React.Component<Props, State> {
    state = {
        billingType: 2,  // opens on the yearly plans
        plans: {1: [], 2: []},
        switchingPlan: null,
        switchedPlan: null,
        paymentDeclined: false,  // the bank declined the charge for the last plan the admin picked (Paddle error 147)
    };

    switchRequested = false;  // set synchronously: a fast double-click must not send two plan changes to Paddle
    upgradeInterval = null;
    upgradeTimeout = null;

    componentDidMount() {
        [1, 2].forEach((billingType) => {
            axios.get(window.SERVER_URL + "/get_all_plans?billing_type=" + billingType).then((res) => {
                const paidPlans = (res.data.result || []).filter((plan) => plan.id !== 1);
                this.setState((prev) => ({plans: {...prev.plans, [billingType]: paidPlans}}));
            }).catch(() => {});
        });
        // The page behind the modal must not scroll while it's open (wheel, touch or keyboard).
        document.documentElement.classList.add("app-scroll-locked");
    }

    componentWillUnmount() {
        this.stopWaiting();
        document.documentElement.classList.remove("app-scroll-locked");
    }

    stopWaiting() {
        clearInterval(this.upgradeInterval);
        clearTimeout(this.upgradeTimeout);
    }

    choosePlan = (plan: Object) => {
        if (this.props.userSettings.is_active === false) {
            // Frozen after a failed payment: a plan change would charge the card that just failed.
            // (SiteWrapper doesn't show the modal to a frozen company; this only guards a settings change mid-session.)
            toast.error("Your last payment failed, so your subscription is frozen. Please reload the page and update your payment method first.");
            return;
        }
        if (this.switchRequested) {
            return;
        }
        this.switchRequested = true;
        this.setState({switchingPlan: plan, paymentDeclined: false});
        axios.post(window.SERVER_URL + "/subscriptions/change_plan", {new_plan_id: plan.id}, XHR_HEADERS).then(() => {
            // Paddle accepted the change; wait for its subscription_updated webhook to move the company to the plan.
            this.upgradeInterval = setInterval(() => {
                axios.post(window.SERVER_URL + "/check_upgrade_status", {newPlanId: plan.id}).then((res) => {
                    if (res.data.upgrade_status === true) {
                        this.stopWaiting();
                        this.setState({switchingPlan: null, switchedPlan: plan});
                    }
                }).catch(() => {});
            }, 1000);
            this.upgradeTimeout = setTimeout(() => {
                this.stopWaiting();
                this.switchRequested = false;
                this.setState({switchingPlan: null});
                toast.error("Your plan change went through, but confirming it is taking longer than expected. Please refresh this page in a minute - if the plan still hasn't updated, contact support.");
            }, UPGRADE_STATUS_TIMEOUT_MS);
        }).catch((err) => {
            this.switchRequested = false;
            this.setState({switchingPlan: null});
            if (isPaymentDeclined(err)) {
                // The modal can't be closed, so "Something went wrong" left the admin stuck (2026-10-12): say what
                // happened, and open Paddle's card-update page straight away (the notice stays behind it, with the
                // button, in case they close it).
                this.setState({paymentDeclined: true});
                this.updatePaymentMethod();
            } else {
                showGenericErrorToast();
            }
        });
    };

    updatePaymentMethod = () => {
        const opened = openPaymentMethodUpdate(this.props.userSettings.update_url, () => {
            this.setState({paymentDeclined: false});
            toast.success("Payment method updated. Please choose your plan again.");
        });
        if (!opened) {
            toast.error("We couldn't open the payment update page. Please contact support.");
        }
        return opened;
    };

    renderPaymentDeclined(): React.Node {
        return (
            <div className="legacy-plan-declined" role="alert">
                <div>
                    <b>Your bank declined the charge.</b> We couldn't charge your card for this plan, so nothing was changed.
                    Please update your payment method, then choose your plan again.
                </div>
                <div className="legacy-plan-declined-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={this.updatePaymentMethod}>Update payment method</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={this.openSupportChat}>Contact support</button>
                </div>
            </div>
        );
    }

    openSupportChat = () => {
        window.$crisp = window.$crisp || [];
        window.$crisp.push(["do", "chat:show"]);
        window.$crisp.push(["do", "chat:open"]);
    };

    renderAccountLinks(): React.Node {
        const disabled = this.state.switchingPlan !== null;
        return (
            <div className="legacy-plan-account-links">
                <button type="button" className="legacy-plan-link" onClick={this.openSupportChat}>Contact support</button>
                <button type="button" className="legacy-plan-link" disabled={disabled} onClick={this.props.onSignOut}>Sign out</button>
            </div>
        );
    }

    renderSwitched(plan: Object): React.Node {
        return (
            <div className="legacy-plan-header" style={{padding: "24px 0"}}>
                <h2 id="legacy-plan-title">You're on {plan.plan_name}! <span role="img" aria-label="celebration">🎉</span></h2>
                <p>Your subscription has been switched to the new plan. Thanks for using Scan2Pay. <span role="img" aria-label="celebration">🎉</span></p>
                <button className="btn btn-primary" style={{marginTop: "20px"}} onClick={() => window.location.reload()}>Continue</button>
            </div>
        );
    }

    render(): React.Node {
        const settings = this.props.userSettings;
        const plans = this.state.plans[this.state.billingType];
        const switching = this.state.switchingPlan;
        const planName = (settings.plan_name || "").trim();
        return (
            <div className="app-modal-overlay legacy-plan-overlay">
                <Confetti />
              <ScaleToFit>
                <div className="app-modal-dialog legacy-plan-modal" role="dialog" aria-modal="true" aria-labelledby="legacy-plan-title">
                    <div className="app-modal-body">
                        {this.state.switchedPlan ? this.renderSwitched(this.state.switchedPlan) : (
                            <React.Fragment>
                                <div className="legacy-plan-header">
                                    <h2 id="legacy-plan-title">QRify is now Scan2Pay! <span role="img" aria-label="celebration">🎉</span></h2>
                                    <p>
                                        The app you know, with a new name, a fresh look and more
                                        power: <span className="legacy-plan-feature">drag-and-drop QR positioning</span> anywhere on your
                                        invoice, <span className="legacy-plan-feature">team access</span> for your whole
                                        company, <span className="legacy-plan-feature">multi-company support</span>, and more.
                                    </p>
                                    <p style={{marginTop: "12px"}}>
                                        Your company is on the legacy <b>{planName}</b>{/plan$/i.test(planName) ? "" : " plan"}, which is
                                        deprecated. Please choose one of our new plans:
                                    </p>
                                </div>
                                <div className="billing-type-toggle" style={{marginBottom: "20px"}}>
                                    <div style={{display: "inline-flex", border: "1px solid #d5dbe3", borderRadius: "8px", overflow: "hidden"}}>
                                        <button className={"btn btn-sm" + (this.state.billingType === 1 ? " btn-primary" : " btn-outline")}
                                                style={{borderRadius: 0}} disabled={switching !== null}
                                                onClick={() => this.setState({billingType: 1})}>Monthly</button>
                                        <button className={"btn btn-sm" + (this.state.billingType === 2 ? " btn-primary" : " btn-outline")}
                                                style={{borderRadius: 0}} disabled={switching !== null}
                                                onClick={() => this.setState({billingType: 2})}>Yearly (Get 2 months free <span role="img" aria-label="celebration">🎉</span>)</button>
                                    </div>
                                </div>
                                {this.state.paymentDeclined ? this.renderPaymentDeclined() : null}
                                <div className="app-grid-row">
                                    {plans.map((plan) => (
                                        <div className="app-grid-col plan-card-col" key={plan.id} style={{flex: "1 1 200px"}}>
                                            <PlanCard plan={plan} button={
                                                <button className="btn btn-primary" disabled={switching !== null} onClick={() => this.choosePlan(plan)}>
                                                    {switching && switching.id === plan.id ? (
                                                        <span style={{display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px"}}>
                                                            <span className="btn-spinner" />
                                                            Switching…
                                                        </span>
                                                    ) : "Choose This Plan"}
                                                </button>
                                            } />
                                        </div>
                                    ))}
                                </div>
                                <p className="legacy-plan-billing-note">
                                    Choosing a plan switches your current subscription immediately. Upgrades are billed right away.
                                </p>
                                {this.renderAccountLinks()}
                            </React.Fragment>
                        )}
                    </div>
                </div>
              </ScaleToFit>
            </div>
        );
    }
}

export default LegacyPlanModal;
